import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import type { DeliveryScheduleItemOut } from '../api/types'
import { DispatchTable } from '../components/DispatchTable'
import { PageHeader } from '../components/Layout'
import { ScheduleDialog } from '../components/ScheduleDialog'
import { SelectionBar } from '../components/SelectionBar'
import { Panel } from '../components/States'
import { isoDate } from '../lib/dates'
import {
  assignToDriver,
  changeToPickup,
  fetchAllItems,
  fetchDrivers,
  forEach,
  setItemDate,
  unschedule,
} from '../lib/deliveries'
import { formatDate, fullName } from '../lib/format'
import { useDispatch } from '../lib/useDispatch'

function shiftDay(day: string, by: number): string {
  const date = new Date(`${day}T00:00:00`)
  date.setDate(date.getDate() + by)
  return isoDate(date)
}

function plural(n: number, word: string) {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

/**
 * One day's dispatch: items with a date but no driver, and each driver's load
 * so far. Only runs that haven't set off can change; anything on the road is
 * on the Today board.
 */
export function DeliveryPlanPage() {
  const [params, setParams] = useSearchParams()
  const today = isoDate(new Date())
  const day = params.get('date') || today
  const [driverId, setDriverId] = useState('')
  const [rescheduling, setRescheduling] = useState(false)
  const dispatch = useDispatch()

  function setDay(next: string) {
    const updated = new URLSearchParams(params)
    if (next && next !== today) updated.set('date', next)
    else updated.delete('date')
    setParams(updated, { replace: true })
    dispatch.clear()
    dispatch.setNotice(null)
  }

  const awaiting = useQuery({
    queryKey: ['dispatch', 'awaiting', day],
    queryFn: () => fetchAllItems({ awaiting_driver: true, scheduled_date: day }),
    refetchInterval: 60_000,
  })
  const assigned = useQuery({
    queryKey: ['dispatch', 'assigned', day],
    queryFn: () => fetchAllItems({ schedule_status: 'assigned', scheduled_date: day }),
    refetchInterval: 60_000,
  })
  const drivers = useQuery({
    queryKey: ['dispatch', 'drivers'],
    queryFn: fetchDrivers,
    staleTime: 10 * 60 * 1000,
  })

  const waiting = awaiting.data ?? []
  const loaded = assigned.data ?? []
  const everything = [...waiting, ...loaded]
  const chosen = everything.filter((item) => dispatch.selected.has(item.id))

  // A run can carry several drivers; the API joins their names.
  const byDriver = new Map<string, DeliveryScheduleItemOut[]>()
  for (const item of loaded) {
    const key = item.driver_name || 'Unknown driver'
    byDriver.set(key, [...(byDriver.get(key) ?? []), item])
  }
  const idle = (drivers.data ?? []).filter(
    (driver) => ![...byDriver.keys()].some((names) => names.includes(fullName(driver.first_name, driver.last_name))),
  )
  const driverName = (id: string) => {
    const driver = drivers.data?.find((d) => d.id === id)
    return driver ? fullName(driver.first_name, driver.last_name) : 'the driver'
  }

  async function assign() {
    if (!driverId) return
    await dispatch
      .run(`${plural(chosen.length, 'item')} assigned to ${driverName(driverId)}.`, () =>
        assignToDriver(chosen.map((item) => item.id), driverId).then(() => undefined),
      )
      .catch(() => undefined)
  }

  async function reschedule(date: string, qtys: Record<string, number>) {
    await dispatch.run(
      `${plural(chosen.length, 'item')} moved to ${formatDate(date)}. They need a driver on that day's plan.`,
      () =>
        forEach(chosen, (item) => {
          const qty = qtys[item.id]
          return setItemDate(item.id, date, qty !== undefined && qty < (item.qty ?? 1) ? qty : undefined)
        }),
    )
    setRescheduling(false)
  }

  async function backToInbox() {
    if (!window.confirm(`Move ${plural(chosen.length, 'item')} back to the inbox? Any driver assignment is removed.`)) return
    await dispatch
      .run(`${plural(chosen.length, 'item')} moved back to the inbox.`, () =>
        forEach(chosen, (item) => unschedule(item.id)),
      )
      .catch(() => undefined)
  }

  async function toPickup() {
    if (!window.confirm(`Change ${plural(chosen.length, 'item')} from delivery to counter pickup?`)) return
    await dispatch
      .run(`${plural(chosen.length, 'item')} changed to counter pickup.`, () => forEach(chosen, changeToPickup))
      .catch(() => undefined)
  }

  return (
    <>
      <PageHeader
        title="Plan a day"
        subtitle={`${formatDate(day)} · ${plural(waiting.length, 'item')} need a driver · ${plural(loaded.length, 'item')} assigned`}
      >
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <button className="btn" onClick={() => setDay(shiftDay(day, -1))} aria-label="Previous day">
            ←
          </button>
          <input type="date" aria-label="Delivery date" value={day} onChange={(event) => setDay(event.target.value)} />
          <button className="btn" onClick={() => setDay(shiftDay(day, 1))} aria-label="Next day">
            →
          </button>
          {day !== today ? (
            <button className="btn ghost" onClick={() => setDay(today)}>
              Today
            </button>
          ) : null}
        </div>
        <SelectionBar
          count={chosen.length}
          busy={dispatch.busy}
          hint="Tick items to assign a driver, reschedule or move them"
          onClear={dispatch.clear}
        >
          <button type="button" className="btn" onClick={toPickup}>
            Change to pickup
          </button>
          <button type="button" className="btn" onClick={backToInbox}>
            Back to inbox
          </button>
          <button type="button" className="btn" onClick={() => setRescheduling(true)}>
            Reschedule…
          </button>
          <select aria-label="Driver" value={driverId} onChange={(event) => setDriverId(event.target.value)}>
            <option value="">Choose a driver…</option>
            {(drivers.data ?? []).map((driver) => (
              <option key={driver.id} value={driver.id}>
                {fullName(driver.first_name, driver.last_name)}
              </option>
            ))}
          </select>
          <button type="button" className="btn primary" onClick={assign} disabled={!driverId}>
            Assign
          </button>
        </SelectionBar>
      </PageHeader>

      <div className="content stack">
        {dispatch.notice ? <div className={`alert ${dispatch.notice.tone}`}>{dispatch.notice.text}</div> : null}

        <div className="card">
          <div className="card-head">
            <h2>Needs a driver</h2>
            <span className="hint">
              New dates come from the <Link to="/deliveries/inbox">inbox</Link>
            </span>
          </div>
          <div className="card-body flush">
            <Panel
              isLoading={awaiting.isPending}
              error={awaiting.error}
              isEmpty={waiting.length === 0}
              emptyText="Nothing waiting for a driver on this day."
            >
              <DispatchTable items={waiting} selected={dispatch.selected} onToggle={dispatch.toggle} />
            </Panel>
          </div>
        </div>

        <Panel isLoading={assigned.isPending} error={assigned.error}>
          {[...byDriver.entries()].map(([names, items]) => (
            <div className="card" key={names}>
              <div className="card-head">
                <h2>{names}</h2>
                <span className="hint">
                  {plural(items.length, 'item')} ·{' '}
                  {[...new Set(items.map((item) => item.delivery_parish || 'No parish'))].join(', ')}
                </span>
              </div>
              <div className="card-body flush">
                <DispatchTable items={items} selected={dispatch.selected} onToggle={dispatch.toggle} />
              </div>
            </div>
          ))}
        </Panel>

        {idle.length ? (
          <p className="hint">
            No items yet: {idle.map((driver) => fullName(driver.first_name, driver.last_name)).join(', ')}. Runs already
            on the road are on the <Link to={`/deliveries/today${day === today ? '' : `?date=${day}`}`}>Today board</Link>.
          </p>
        ) : null}
      </div>

      {rescheduling ? (
        <ScheduleDialog
          title={`Reschedule ${plural(chosen.length, 'item')}`}
          confirmLabel="Reschedule"
          items={chosen}
          onCancel={() => setRescheduling(false)}
          onConfirm={reschedule}
        />
      ) : null}
    </>
  )
}
